import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AcceptInvitationStepBase } from '../../shared/accept-invitation-step-base/accept-invitation-step-base';
import { environment } from '../../environments/environment';
import usStates from '../../shared/data/us-states.json';
import { dateValidator } from '../../utils/date-validator';
import { formatStoredDate } from '../../utils/format-stored-date';

@Component({
  selector: 'app-additional-owner-info',
  templateUrl: './additional-owner-info.component.html',
  styleUrl: './additional-owner-info.component.scss'
})
export class AdditionalOwnerInfoComponent extends AcceptInvitationStepBase implements OnInit {
  public override readonly storageKey = 'additionalOwnerInfo';
  public override readonly nextUrl = 'additional-owner-address';
  public override readonly previousUrl = 'additional-owners';
  public readonly usStates = usStates;
  public override readonly form: FormGroup;
  // TODO: move this to a service
  public readonly ssnReplacePattern = { 'X': { pattern: new RegExp(/\d/), symbol: '●' } };
  
  public ssnMasked: boolean = true;
  private ownerId: string | null = null;

  public constructor() {
    super();
    const fb = inject(FormBuilder);
    this.form = fb.group({
      id: [''],
      firstName: ['', Validators.required],
      middleInitial: ['', [Validators.pattern(/^[A-Za-z]$/)]],
      lastName: ['', Validators.required],
      emailAddress: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      dateOfBirth: ['', [Validators.required, dateValidator]],
      // No explicit pattern validator needed here, ngx-mask handles it and adds 'mask' error
      ssn: ['', [Validators.required, Validators.pattern(/^\d{9}$/)]], 
    });
  }

  public override ngOnInit(): void {
    // Get owner ID from route if it exists
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.ownerId = id;
        this.loadOwnerData(id);
      } else {
        // If no ID, generate a new one
        this.ownerId = this.generateUniqueId();
        this.form.get('id')?.setValue(this.ownerId);
      }
    });

    // Check if we're coming from additional-owners with data
    this.route.queryParams.subscribe(params => {
      if (params['owner']) {
        try {
          const owner = JSON.parse(params['owner']);
          this.form.patchValue(owner);
        } catch (e) {
          console.error('Error processing the owner object:', e);
        }
      }
    });

    // On form changes, persist form data
    this.form.valueChanges.subscribe(() => {
      this.saveFormData();
    });
  }

  private loadOwnerData(id: string) {
    const ownerData = this.dataStorageService.getItem(`owner_${id}`);
    if (ownerData) {
      this.form.patchValue(ownerData);
    }
  }

  public override async continue() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saveFormData();
    
    // Navigate to next screen
    if (this.ownerId) {
      await this.router.navigate([`${this.nextUrl}/${this.ownerId}`], { 
        relativeTo: this.route.parent
      });
    }
  }

  public override async goBack(): Promise<void> {
    this.router.navigate([this.previousUrl], { relativeTo: this.route.parent });
  }
  // TODO: move this to a service
  private generateUniqueId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }

  // TODO: move this to a service
  public onlyNumberKey(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

 
  public toggleSSNVisibility(event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.ssnMasked = !this.ssnMasked;
  }

  private saveFormData(): void {
    const formValue = this.form.value;
    formValue.dateOfBirth = formatStoredDate(formValue.dateOfBirth);
    
    // Save owner data to dataStorage without losing address data
    const existingOwnerData = this.dataStorageService.getItem(`owner_${this.ownerId}`) || {};
    const updatedOwnerData = { ...existingOwnerData, ...formValue, isOwner: true };
    this.dataStorageService.setItem(`owner_${this.ownerId}`, updatedOwnerData);
  }
}
